/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { HardwareAssetsFile } from "../../models/http/dashboard/hardware-assets-file.model";
import { HardwareAssetsGeneralInformation } from "../../models/http/dashboard/hardware-assets-general-information.model";
import { ManufacturerEmission } from "../../models/http/dashboard/manufacturer-emission.model";
import { PollingStatus } from "../../models/http/what-if/scenario/scenario-full.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { EmissionViewMode } from "../../models/ui/emission-view-mode.model";
import { HardwareAssetsScopeMode } from "../../models/ui/hardware-assets-scope-mode.model";
import { HardwareAssetsScopeOrderBy } from "../../models/ui/hardware-assets-scope-orderby.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { TableResponse } from "../../models/ui/table-response.model";
import { CalculationKpis } from "../../models/http/calculator/calculation-kpis.model";
import { CalculatorHardwareAssetsForm } from "../../models/http/dashboard/calculator-hardware-assets-form.model";
import { HardwareAssetsOverview } from "../../models/http/dashboard/hardware-assets-overview.model";
import { SelectOption } from "../../models/ui/select-option.model";
import { roundValueUpTo } from "../../utils/utils";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";
import { isNullOrUndefined } from "../../utils/utils";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";

@Injectable({
	providedIn: "root"
})
export class DashboardHardwareAssetsHttpService {
	private _dashboardHardwareAssetsOperations = ENDPOINT_GROUPS.dashboardHardwareAssets.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Renames a file associated with a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be renamed.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @param name - The new name to assign to the file.
	 * @returns An observable that emits the response as a string upon successful renaming.
	 */
	public renameFile(fileId: string, tenantId: string, name: string): Observable<string> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.renameFile.service,
			HttpMethods.PUT,
			{ params, pathParams },
			{ name }
		);
	}

	/**
	 * Deletes a file associated with a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be deleted.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @returns An observable that emits the response as a string upon successful deletion.
	 */
	public deleteFile(fileId: string, tenantId: string): Observable<string> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.deleteFile.service,
			HttpMethods.DELETE,
			{ params, pathParams }
		);
	}

	/**
	 * Uploads a file associated with a specific tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param file - The file to be uploaded, wrapped in a FormData object.
	 * @param isTemporary - Optional flag indicating if the upload is temporary.
	 * @returns An Observable emitting an object containing the uploaded file's ID.
	 */
	public uploadFile(
		tenantId: string,
		file: FormData,
		isTemporary?: boolean
	): Observable<{ fileId: string }> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				...(!isNullOrUndefined(isTemporary) && { isTemporary })
			}
		});

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.uploadFile.service,
			HttpMethods.POST,
			{ params },
			file
		);
	}

	/**
	 * Initiates the download of hardware asset data as a CSV file for a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be downloaded.
	 * @param tenantId - The unique identifier of the tenant requesting the download.
	 * @returns An Observable that emits the CSV file content as a string.
	 */
	public downloadAsCsv(fileId: string, tenantId: string): Observable<string> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.downloadAsCsv.service,
			HttpMethods.POST,
			{ params, pathParams, responseType: "text" }
		);
	}

	/**
	 * Retrieves the uploading status of a file for a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file whose uploading status is being queried.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @returns An observable that emits the polling status of the file upload.
	 */
	public getUploadingStatus(
		fileId: string,
		tenantId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getUploadingStatus.service,
			HttpMethods.GET,
			{ params, pathParams }
		);
	}

	/**
	 * Downloads a template file from the server.
	 *
	 * @returns An `Observable` that emits a `Blob` representing the downloaded template file.
	 */
	public downloadTemplate(): Observable<Blob> {
		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.downloadTemplate.service,
			HttpMethods.GET,
			{ responseType: "blob" }
		);
	}

	/**
	 * Retrieves a list of device files associated with a specific tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant for which the files are to be retrieved.
	 * @returns An observable that emits an array of `HardwareAssetsFile` objects.
	 */
	public getFiles(tenantId: string): Observable<{ files: HardwareAssetsFile[] }> {
		const pathParams = {
			tenantId
		};

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getFiles.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Retrieves a list of device categories from the dashboard service.
	 *
	 * @param tenantId - The unique identifier of the tenant for which the files are to be retrieved.
	 * @returns An observable that emits an array of category strings.
	 */
	public getCategories(tenantId: string): Observable<{ categories: string[] }> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getCategories.service,
			HttpMethods.GET,
			{ params }
		);
	}

	/**
	 * Retrieves general information about hardware assets based on the provided file ID and tenant ID.
	 *
	 * @param fileId - The unique identifier of the file for which general information is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits the general information of hardware assets as a `HardwareAssetsGeneralInformation` object.
	 */
	public getGeneralInformation(
		fileId: string,
		tenantId: string
	): Observable<HardwareAssetsGeneralInformation> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getGeneralInformation.service,
			HttpMethods.GET,
			{ params, pathParams }
		);
	}

	/**
	 * Retrieves the total emissions data for a pie chart visualization.
	 *
	 * @param fileId - The unique identifier of the file containing the data.
	 * @param tenantId - The unique identifier of the tenant requesting the data.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable emitting a `ChartResponse` containing the pie chart data or an error state.
	 *
	 * The method constructs HTTP parameters and path parameters, then makes a GET request
	 * using the `_communicationService`. The response is mapped to a `ChartResponse` object
	 * containing the pie chart data. If an error occurs during the request, it returns a
	 * default error state with an empty pie chart object.
	 */
	public getTotalEmissionForPieChart(
		fileId: string,
		tenantId: string,
		categories: string[]
	): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getTotalEmissionForPieChart.service,
				HttpMethods.POST,
				{ params, pathParams },
				{ categories }
			)
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	public getEmissionsByModel(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsByModel.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsByModelPerDevice(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsByModelPerDevice.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsByRegion(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsByRegion.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsOverTime(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});
		const pathParams = { fileId };
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsOverTime.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getSavedLifecycleEmissions(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getSavedLifecycleEmissions.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getEmissionsByCountryOverTime(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsByCountryOverTime.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the data about the total amount of co2 generated by each group of machines
	 * (where a group is defined by the installation status) for a pie chart visualization.
	 *
	 * @param fileId - The unique identifier of the file containing the data.
	 * @param tenantId - The unique identifier of the tenant requesting the data.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable emitting a `ChartResponse` containing the pie chart data or an error state.
	 *
	 * The method constructs HTTP parameters and path parameters, then makes a GET request
	 * using the `_communicationService`. The response is mapped to a `ChartResponse` object
	 * containing the pie chart data. If an error occurs during the request, it returns a
	 * default error state with an empty pie chart object.
	 */
	public getCo2ByInstallStatusForPieChart(fileId: string): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getCo2ByInstallStatus.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => {
					console.log(
						"Error occurred while fetching CO2 by installation status for pie chart"
					);
					return of({ chart: {} as PieChart, hasError: true });
				})
			);
	}

	/**
	 * Retrieves the manufacturers' emissions data for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file.
	 * @param tenantId - The unique identifier of the tenant.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable that emits an array of `ManufacturerEmission` objects.
	 *          If an error occurs, an empty array is returned.
	 */
	public getManufacturersEmission(
		fileId: string,
		tenantId: string,
		categories: string[]
	): Observable<TableResponse<ManufacturerEmission[]>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getManufacturersEmission.service,
				HttpMethods.POST,
				{ params, pathParams },
				{ categories }
			)
			.pipe(
				map(
					({
						manufacturersEmissions
					}: {
						manufacturersEmissions: ManufacturerEmission[];
					}) => {
						const mappedManufacturersEmissions = manufacturersEmissions.map(
							manufacturer => ({
								...manufacturer,
								emissionValue: roundValueUpTo(manufacturer.emissionValue)
							})
						);

						return {
							rowData: mappedManufacturersEmissions,
							hasError: false
						};
					}
				),
				catchError(() => of({ rowData: [], hasError: true }))
			);
	}

	/**
	 * Retrieves the carbon footprint data for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which the carbon footprint is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits a `ChartResponse` containing a `BarChart` object if successful,
	 *          or an object with an empty `BarChart` and `hasError` set to true in case of an error.
	 */
	public getCarbonFootprint(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getCarbonFootprint.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the carbon footprint data by scope for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which the carbon footprint data is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the data.
	 * @param mode - The mode specifying the scope of the hardware assets (e.g., specific filtering criteria).
	 * @param orderBy - The ordering criteria for the data (e.g., by date, value, etc.).
	 * @returns An observable that emits a `ChartResponse` containing a `BarChart` object with the carbon footprint data.
	 *          If an error occurs, the observable emits a response with an empty chart and `hasError` set to true.
	 */
	public getCarbonFootprintByScope(
		fileId: string,
		tenantId: string,
		mode: HardwareAssetsScopeMode,
		orderBy: HardwareAssetsScopeOrderBy,
		manufacturers: string[]
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				mode,
				orderBy
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getCarbonFootprintByScope.service,
				HttpMethods.POST,
				{ params, pathParams },
				{ manufacturers }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves a list of manufacturers associated with a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which manufacturers are being retrieved.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits an array of manufacturer names.
	 */
	public getManufacturers(
		fileId: string,
		tenantId: string
	): Observable<TableResponse<{ manufacturer: string }[]>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getManufacturers.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(({ manufacturers }: { manufacturers: string[] }) => ({
					rowData: manufacturers.map(manufacturer => ({
						manufacturer
					})),
					hasError: false
				})),
				catchError(() => of({ rowData: [], hasError: true }))
			);
	}

	/**
	 * Retrieves a list of all available countries.
	 *
	 * @returns An Observable that emits an object containing an array of country names.
	 */
	public getAllCountries(): Observable<SelectOption[]> {
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getAllCountries.service,
				HttpMethods.GET
			)
			.pipe(map(({ locations }: { locations: SelectOption[] }) => locations));
	}

	/**
	 * Retrieves a list of all hardware asset manufacturers.
	 *
	 * @returns An Observable that emits an object containing an array of manufacturer names.
	 */
	public getAllManufacturers(): Observable<{ manufacturers: string[] }> {
		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getAllManufacturers.service,
			HttpMethods.GET
		);
	}

	public getEmissionsByRegionPerDevice(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { fileId };
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionsByRegionPerDevice.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Computes hardware asset calculations by sending the provided form data to the backend service.
	 *
	 * @param hardwareAssets - The form data containing hardware assets information to be computed.
	 * @returns An Observable emitting an object with the generated calculation ID.
	 */
	public compute(
		hardwareAssets: CalculatorHardwareAssetsForm[]
	): Observable<{ calculationId: string }> {
		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.compute.service,
			HttpMethods.POST,
			{},
			{ devices: hardwareAssets }
		);
	}

	/**
	 * Retrieves the list of general hardware asset categories.
	 *
	 * @returns An Observable that emits an object containing an array of category names.
	 */
	public getGeneralCategories(): Observable<{ categories: string[] }> {
		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getGeneralCategories.service,
			HttpMethods.GET
		);
	}

	/**
	 * Retrieves specific hardware asset categories based on the provided category name.
	 *
	 * @param category - The name of the category to filter hardware asset categories.
	 * @returns An Observable emitting an object containing an array of category names.
	 */
	public getSpecificCategories(category: string): Observable<{ categories: string[] }> {
		const pathParams = { category };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getSpecificCategories.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Retrieves the total emission data for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions.
	 */
	public getTotalEmission(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getTotalEmission.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the emission overview for hardware assets based on the specified calculation and tenant IDs.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting an array of `HardwareAssetsOverview` objects representing the emission overview.
	 */
	public getEmissionOverview(
		calculationId: string,
		tenantId: string
	): Observable<HardwareAssetsOverview[]> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionOverview.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(map(({ devices }: { devices: HardwareAssetsOverview[] }) => devices));
	}

	/**
	 * Retrieves the calculation KPIs for a specific calculation and tenant.
	 *
	 * @param calculationId - The unique identifier of the calculation.
	 * @param tenantId - The unique identifier of the tenant.
	 * @returns An Observable emitting the calculation KPIs.
	 */
	public getCalculationKpis(
		calculationId: string,
		tenantId: string
	): Observable<CalculationKpis> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getCalculationKpis.service,
			HttpMethods.GET,
			{ params, pathParams }
		);
	}

	/**
	 * Retrieves emission data categorized by scope for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a ChartResponse containing PieChart data representing emissions by scope.
	 */
	public getEmissionByScope(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionByScope.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	/**
	 * Retrieves emission data grouped by manufacturer for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a PieChart representing emissions by manufacturer.
	 */
	public getEmissionByManufacturer(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionByManufacturer.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	/**
	 * Saves a hardware asset calculation for a specific tenant.
	 *
	 * @param calculationId - The unique identifier of the calculation to be saved.
	 * @param tenantId - The unique identifier of the tenant for whom the calculation is being saved.
	 * @returns An Observable that emits an object containing the generated fileId upon successful save.
	 */
	public saveCalculation(
		calculationId: string,
		tenantId: string
	): Observable<{ fileId: string }> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { calculationId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.saveCalculation.service,
			HttpMethods.POST,
			{ params, pathParams }
		);
	}

	/**
	 * Deletes a calculation by its unique identifier.
	 *
	 * @param calculationId - The unique identifier of the calculation to delete.
	 * @returns An Observable that emits the ID of the deleted calculation upon successful deletion.
	 */
	public deleteCalculation(calculationId: string): Observable<string> {
		const pathParams = { calculationId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.deleteCalculation.service,
			HttpMethods.DELETE,
			{ pathParams }
		);
	}

	/**
	 * Retrieves the calculation status for a given calculation ID.
	 *
	 * @param calculationId - The unique identifier of the calculation whose status is to be retrieved.
	 * @returns An Observable emitting an object containing the current polling status and an optional message.
	 */
	public getCalculationStatus(
		calculationId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		const pathParams = { calculationId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getCalculationStatus.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Retrieves a scenario identifier based on the provided list of file names.
	 *
	 * @param files - An array of file names to be used for scenario retrieval.
	 * @returns An Observable that emits an object containing the `scenarioId` as a string.
	 */
	public getScenario(files: string[]): Observable<{ scenarioId: string }> {
		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getScenario.service,
			HttpMethods.POST,
			{},
			{ files }
		);
	}

	/**
	 * Retrieves the status of a specific scenario for a given tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario whose status is to be fetched.
	 * @param tenantId - The unique identifier of the tenant associated with the scenario.
	 * @returns An Observable emitting an object containing the scenario's polling status and an optional message.
	 */
	public getScenarioStatus(
		scenarioId: string,
		tenantId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { scenarioId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.getScenarioStatus.service,
			HttpMethods.GET,
			{ params, pathParams }
		);
	}

	/**
	 * Deletes a hardware asset scenario for a specific tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario to delete.
	 * @param tenantId - The unique identifier of the tenant associated with the scenario.
	 * @returns An Observable that emits the ID of the deleted scenario upon successful deletion.
	 */
	public deleteScenario(scenarioId: string, tenantId: string): Observable<string> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { scenarioId };

		return this._communicationService.callService(
			this._dashboardHardwareAssetsOperations.deleteScenario.service,
			HttpMethods.DELETE,
			{ params, pathParams }
		);
	}

	/**
	 * Retrieves the emission overview data for a specific scenario and tenant, formatted for a bar chart.
	 *
	 * @param scenarioId - The unique identifier of the scenario to retrieve emission data for.
	 * @param tenantId - The unique identifier of the tenant whose emission data is requested.
	 * @param viewMode - The mode in which the emission data should be viewed (e.g., by category, by time period).
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing the emission overview.
	 */
	public getEmissionOverviewForBarChart(
		scenarioId: string,
		tenantId: string,
		viewMode: EmissionViewMode
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				viewMode
			}
		});

		const pathParams = { scenarioId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getEmissionOverviewForBarChart.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the total emission data grouped by category for a given scenario and tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario for which emissions are to be fetched.
	 * @param tenantId - The unique identifier of the tenant.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions by category.
	 */
	public getTotalEmissionByCategory(
		scenarioId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId
			}
		});

		const pathParams = { scenarioId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getTotalEmissionByCategory.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the total emissions data grouped by manufacturer for a given scenario and tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario to retrieve data for.
	 * @param tenantId - The unique identifier of the tenant.
	 * @param mode - The scope mode for hardware assets (e.g., global, local).
	 * @param orderBy - The ordering criteria for the results.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions by manufacturer.
	 */
	public getTotalEmissionByManufacturer(
		scenarioId: string,
		tenantId: string,
		mode: HardwareAssetsScopeMode,
		orderBy: HardwareAssetsScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				mode,
				orderBy
			}
		});

		const pathParams = { scenarioId };

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsOperations.getTotalEmissionByManufacturer.service,
				HttpMethods.GET,
				{ params, pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}
}
